import React, {useCallback, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useDatelessSeoSRP} from 'hooks/useDatelessSeoSRP'
import {useDatePickerTiming} from 'hooks/useDatePickerTiming'
import {useDestinationDisplayName} from 'hooks/useDestinationDisplayName'
import {useScrollPosition} from 'hooks/useScrollPosition'
import useShowOverlay, {
  useShowDatePickerOverlay
} from 'hooks/useShowOverlay/useShowOverlay'
import {trackTimingEvent} from 'middleware/analytics'
import {OverlayType} from 'modules/overlay/types'
import {
  getSearchBoxValues,
  getShowRoomsLabel
} from 'modules/searchBox/selectors'
import {closeSearchBox} from 'modules/searchBox/slice'

import {scrollToWindowTop} from '@vio/core/src/_web/utils/browser'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {DatePickerType} from '@vio/core/src/datePicker/types'
import {roomsToConfigurationObject} from '@vio/core/src/room/business/roomConfiguration'
import {SearchFormVertical} from '@vio/shared/src/search/SearchFormVertical'

interface Props {
  onDestinationPickerClick?: () => void
}

export const FakeSearchFormWithStore: React.FC<Props> = ({
  onDestinationPickerClick
}) => {
  const isDatelessSeoSRP = useDatelessSeoSRP()

  const dispatch = useDispatch()
  const showDatePickerOverlay = useShowDatePickerOverlay()
  const {datePickerLoadedTimerStart} = useDatePickerTiming()
  const showOverlay = useShowOverlay()

  const {checkIn, checkOut, rooms} = useSelector(getSearchBoxValues)
  const destination = useDestinationDisplayName()
  const {numberOfAdults, numberOfChildren, numberOfRooms} =
    roomsToConfigurationObject(rooms)

  const showRoomsLabel = useSelector(getShowRoomsLabel)
  const {scrollY} = useScrollPosition()

  useEffect(() => {
    trackTimingEvent(
      dispatch,
      'SearchBoxReady',
      'SearchBoxReady-start',
      Entity.SearchBox
    )
  }, [dispatch])

  const handleDestinationPickerClick = useCallback(() => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.DestinationPickerInput,
        action: Action.Clicked
      })
    )
    showOverlay({
      type: OverlayType.DestinationPicker
    })
    onDestinationPickerClick?.()
  }, [dispatch, onDestinationPickerClick, showOverlay])

  const handleDatePickerClick = useCallback(
    (type: DatePickerType) => {
      datePickerLoadedTimerStart()

      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.DatePickerInput,
          action: Action.Clicked,
          component: type
        })
      )
      showDatePickerOverlay(type)
    },
    [dispatch, datePickerLoadedTimerStart, showDatePickerOverlay]
  )

  const handleGuestPickerClick = useCallback(() => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.GuestPickerInput,
        action: Action.Clicked
      })
    )
    showOverlay({
      type: OverlayType.GuestPicker,
      entity: Entity.GuestPicker
    })
  }, [dispatch, showOverlay])

  const handleClose = useCallback(() => {
    if (scrollY > 0) {
      scrollToWindowTop()
    }

    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.SearchBoxSearchButton,
        action: Action.Clicked
      })
    )
    dispatch(closeSearchBox())
  }, [dispatch, scrollY])

  return (
    <SearchFormVertical
      destination={destination}
      numberOfAdults={numberOfAdults}
      numberOfChildren={numberOfChildren}
      numberOfRooms={numberOfRooms}
      onDatePickerClick={handleDatePickerClick}
      onDestinationPickerClick={handleDestinationPickerClick}
      onGuestPickerClick={handleGuestPickerClick}
      onSubmit={handleClose}
      isRoomsLabelShown={showRoomsLabel}
      checkIn={isDatelessSeoSRP ? undefined : checkIn}
      checkOut={isDatelessSeoSRP ? undefined : checkOut}
    />
  )
}
